/**
 * Diffusion color-cast matcher + applier.
 *
 * Phase 2.B.2 — leverage existing applyLook() pipeline. Each cast in
 * `diffusion-casts.js` is expressed as a partial GRADE_DEFAULTS
 * delta that can be merged via applyLook() with no kernel changes.
 * Pure-JS, no models, no external calls — privacy-first stays intact.
 *
 * Matching strategy:
 *   1. If we have a `model` field (from A1111 parameters), substring-
 *      match against each cast's matchPatterns. First match wins.
 *   2. Else fall back to vendor (a1111 / comfyui / midjourney) by
 *      mapping vendor → most-likely cast ID.
 *   3. No match → null (caller decides what to do; UI shows no
 *      "Apply correction" button when null).
 *
 * @module src/pipeline/diffusion-cast
 */

/* Data import — plain ESM JS data module. Migrated from a .json file
 * (Phase 0 hardening per Codex GO Phase 0.harden) to avoid
 * `with { type: 'json' }` import-attribute fragility: Vite 5.4 strips
 * the attribute via its `?import` query rewrite (works), Node 22.12
 * REQUIRES the attribute on bare JSON, and the syntax is unstable
 * across esbuild 0.21 → 0.22. JS data module sidesteps all of it. */
import casts from './diffusion-casts.js';

const VENDOR_DEFAULT_CAST = {
    a1111:      'sdxl',                /* most A1111 users run SDXL today */
    comfyui:    'comfyui-generic',
    midjourney: 'mj-v6',
};

/**
 * Match a cast for the given AI metadata struct (from
 * extractAIMetadata). Returns the cast object or null.
 *
 * @param {object | null} meta  output of extractAIMetadata()
 * @returns {object | null}     full cast entry (id, name, grade, …)
 */
export function matchCast(meta) {
    if (!meta || !meta.present) return null;
    const list = casts.casts || [];
    /* 1. Model-string matching. Case-insensitive substring. */
    if (meta.model) {
        const m = meta.model.toLowerCase();
        for (const cast of list) {
            for (const pat of cast.matchPatterns || []) {
                if (m.includes(pat.toLowerCase())) return cast;
            }
        }
    }
    /* 2. Vendor fallback. */
    if (meta.vendor) {
        const fallbackId = VENDOR_DEFAULT_CAST[meta.vendor];
        if (fallbackId) {
            const cast = list.find(c => c.id === fallbackId);
            if (cast) return cast;
        }
    }
    return null;
}

/**
 * Build a "look" object compatible with the legacy editor's
 * applyLook(look) signature. The editor expects:
 *   { name, adjust?, grade?, curves?, curveIntensity? }
 * For a cast we only set name + grade (other channels stay at
 * defaults, which preserves the user's neutral starting point —
 * though applyLook DOES reset adjust/curves/curveIntensity to
 * defaults, so callers should warn the user about that).
 *
 * @param {object} cast  cast entry from matchCast()
 * @returns {object | null}
 */
export function castToLook(cast) {
    if (!cast || !cast.grade) return null;
    return {
        name: cast.name,
        grade: { ...cast.grade },
    };
}

/** Look up a cast by id directly. Used by tests + direct UI overrides. */
export function getCastById(id) {
    if (!id) return null;
    return (casts.casts || []).find(c => c.id === id) || null;
}

/** All cast entries — for tests + future "manual cast picker" UI. */
export function listCasts() {
    return (casts.casts || []).slice();
}
